<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(changePassword)">
        <slot></slot>

        <!-- Old Password Field -->
        <ValidationProvider
          v-if="askOldPassword"
          rules="required"
          vid="oldPassword"
          v-slot="{ errors, valid }"
        >
          <b-field
            :label="$t('password_change.label.old_password')"
            :message="errors"
            :type="{ 'is-danger': errors[0], '': valid }"
            :custom-class="labelClass"
          >
            <b-input
              v-model="oldPassword"
              type="password"
              icon="lock"
              placeholder="Password"
            ></b-input>
          </b-field>
        </ValidationProvider>

        <!-- New Password Field with Policy Validation -->
        <ValidationProvider
          :rules="newPasswordRules"
          vid="password"
          v-slot="{ errors, valid }"
        >
          <b-field
            :label="$t('password_change.label.new_password')"
            :message="errors.length > 0 ? errors : passwordValidationMessages"
            :type="{
              'is-danger': errors[0] || !isPasswordPolicyCompliant,
              'is-success':
                valid && isPasswordPolicyCompliant && newPassword.length > 0,
            }"
            :custom-class="labelClass"
          >
            <b-input
              v-model="newPassword"
              type="password"
              icon="lock"
              placeholder="Password"
              @input="validatePasswordPolicy"
            ></b-input>
          </b-field>
        </ValidationProvider>

        <!-- Password Policy Compliance Indicators -->
        <div
          v-if="passwordPolicy && newPassword.length > 0"
          class="password-policy-indicators mb-3"
        >
          <div class="content is-small">
            <p class="has-text-weight-semibold mb-2">
              {{ $t("password_change.policy_compliance") }}:
            </p>
            <ul class="policy-checklist">
              <li :class="getPolicyCheckClass('minLength')">
                <b-icon
                  :icon="getPolicyCheckIcon('minLength')"
                  size="is-small"
                  :type="getPolicyCheckType('minLength')"
                ></b-icon>
                {{
                  $t("password_change.min_length", {
                    length: passwordPolicy.minLength,
                  })
                }}
              </li>
              <li
                v-if="passwordPolicy.requireUppercase"
                :class="getPolicyCheckClass('uppercase')"
              >
                <b-icon
                  :icon="getPolicyCheckIcon('uppercase')"
                  size="is-small"
                  :type="getPolicyCheckType('uppercase')"
                ></b-icon>
                {{ $t("password_change.require_uppercase") }}
              </li>
              <li
                v-if="passwordPolicy.requireLowercase"
                :class="getPolicyCheckClass('lowercase')"
              >
                <b-icon
                  :icon="getPolicyCheckIcon('lowercase')"
                  size="is-small"
                  :type="getPolicyCheckType('lowercase')"
                ></b-icon>
                {{ $t("password_change.require_lowercase") }}
              </li>
              <li
                v-if="passwordPolicy.requireNumbers"
                :class="getPolicyCheckClass('numbers')"
              >
                <b-icon
                  :icon="getPolicyCheckIcon('numbers')"
                  size="is-small"
                  :type="getPolicyCheckType('numbers')"
                ></b-icon>
                {{ $t("password_change.require_numbers") }}
              </li>
              <li
                v-if="passwordPolicy.requireSpecialChars"
                :class="getPolicyCheckClass('specialChars')"
              >
                <b-icon
                  :icon="getPolicyCheckIcon('specialChars')"
                  size="is-small"
                  :type="getPolicyCheckType('specialChars')"
                ></b-icon>
                {{ $t("password_change.require_special_chars") }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Confirm Password Field -->
        <ValidationProvider
          name="confirm"
          rules="required|confirmed:password"
          vid="confirmation"
          v-slot="{ errors, valid }"
        >
          <b-field
            :label="$t('password_change.label.confirm_new_password')"
            :message="errors"
            :type="{
              'is-danger': errors[0],
              'is-success': valid && confirmNewPassword.length > 0,
            }"
            :custom-class="labelClass"
          >
            <b-input
              v-model="confirmNewPassword"
              type="password"
              icon="lock"
              placeholder="Password"
              lazy
            ></b-input>
          </b-field>
        </ValidationProvider>

        <b-button
          native-type="submit"
          class="is-primary mt-3"
          :disabled="!isFormValid"
        >
          {{ $t("password_change.buttons.change") }}
        </b-button>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    askOldPassword: {
      type: Boolean,
      default: true,
    },
    labelClass: {
      type: String,
      default: "",
    },
    isEnforced: {
      type: Boolean,
      default: false,
    },
    passwordPolicy: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      policyCompliance: {
        minLength: false,
        uppercase: false,
        lowercase: false,
        numbers: false,
        specialChars: false,
      },
    };
  },
  computed: {
    // Use provided password policy
    effectivePasswordPolicy() {
      return this.passwordPolicy;
    },

    // Dynamic validation rules based on password policy
    newPasswordRules() {
      let rules = "required";

      if (this.effectivePasswordPolicy) {
        // Add minimum length rule
        if (this.effectivePasswordPolicy.minLength) {
          rules += `|min:${this.effectivePasswordPolicy.minLength}`;
        }

        // Add custom policy validation rule
        rules += "|password_policy";
      }

      return rules;
    },

    // Check if password meets all policy requirements
    isPasswordPolicyCompliant() {
      if (!this.effectivePasswordPolicy || !this.newPassword) {
        return false;
      }

      return Object.values(this.policyCompliance).every(
        (compliant) => compliant
      );
    },

    // Generate validation messages for policy compliance
    passwordValidationMessages() {
      if (
        !this.effectivePasswordPolicy ||
        !this.newPassword ||
        this.isPasswordPolicyCompliant
      ) {
        return [];
      }

      const messages = [];
      const policy = this.effectivePasswordPolicy;

      if (!this.policyCompliance.minLength && policy.minLength) {
        messages.push(
          this.$t("password_change.validation.min_length", {
            length: policy.minLength,
          })
        );
      }
      if (!this.policyCompliance.uppercase && policy.requireUppercase) {
        messages.push(this.$t("password_change.validation.require_uppercase"));
      }
      if (!this.policyCompliance.lowercase && policy.requireLowercase) {
        messages.push(this.$t("password_change.validation.require_lowercase"));
      }
      if (!this.policyCompliance.numbers && policy.requireNumbers) {
        messages.push(this.$t("password_change.validation.require_numbers"));
      }
      if (!this.policyCompliance.specialChars && policy.requireSpecialChars) {
        messages.push(
          this.$t("password_change.validation.require_special_chars")
        );
      }

      return messages;
    },

    // Check if the entire form is valid for submission
    isFormValid() {
      // For enforced changes with temporary passwords, old password is not required
      const oldPasswordRequired =
        this.askOldPassword && !this.isTemporaryPasswordScenario;
      const hasOldPassword =
        !oldPasswordRequired || this.oldPassword.length > 0;

      const hasNewPassword = this.newPassword.length > 0;
      const hasConfirmPassword = this.confirmNewPassword.length > 0;
      const passwordsMatch = this.newPassword === this.confirmNewPassword;

      // Policy compliance is always mandatory when a policy is defined
      const policyCompliant =
        !this.effectivePasswordPolicy || this.isPasswordPolicyCompliant;

      // For enforced changes, ensure new password differs from old (if old password provided)
      const passwordsDiffer =
        !this.isEnforced ||
        !this.oldPassword ||
        this.oldPassword !== this.newPassword;

      return (
        hasOldPassword &&
        hasNewPassword &&
        hasConfirmPassword &&
        passwordsMatch &&
        policyCompliant &&
        passwordsDiffer
      );
    },

    // Check if this is a temporary password scenario (no old password required)
    isTemporaryPasswordScenario() {
      return this.isEnforced && !this.askOldPassword;
    },
  },
  watch: {
    // Watch for password policy changes and validate current password
    effectivePasswordPolicy: {
      handler() {
        if (this.newPassword) {
          this.validatePasswordPolicy();
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Register custom validation rule for password policy
    this.registerPasswordPolicyValidation();
  },
  methods: {
    // Register custom vee-validate rule for password policy compliance
    registerPasswordPolicyValidation() {
      const { extend } = require("vee-validate");

      extend("password_policy", {
        validate: (value) => {
          if (!this.effectivePasswordPolicy) {
            return true;
          }

          this.validatePasswordPolicy(value);
          return this.isPasswordPolicyCompliant;
        },
        message: () => {
          const messages = this.passwordValidationMessages;
          return messages.length > 0
            ? messages[0]
            : this.$t("password_change.validation.policy_not_met");
        },
      });
    },

    // Validate password against policy and update compliance state
    validatePasswordPolicy(password = null) {
      const pwd = password || this.newPassword;
      const policy = this.effectivePasswordPolicy;

      if (!policy || !pwd) {
        // Reset compliance if no policy or password
        Object.keys(this.policyCompliance).forEach((key) => {
          this.policyCompliance[key] = false;
        });
        return;
      }

      // Check minimum length
      this.policyCompliance.minLength =
        !policy.minLength || pwd.length >= policy.minLength;

      // Check uppercase requirement
      this.policyCompliance.uppercase =
        !policy.requireUppercase || /[A-Z]/.test(pwd);

      // Check lowercase requirement
      this.policyCompliance.lowercase =
        !policy.requireLowercase || /[a-z]/.test(pwd);

      // Check numbers requirement
      this.policyCompliance.numbers = !policy.requireNumbers || /\d/.test(pwd);

      // Check special characters requirement
      this.policyCompliance.specialChars =
        !policy.requireSpecialChars || /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    },

    // Get CSS class for policy check items
    getPolicyCheckClass(requirement) {
      const isCompliant = this.policyCompliance[requirement];
      return {
        "has-text-success": isCompliant,
        "has-text-danger": !isCompliant,
        "policy-check-item": true,
      };
    },

    // Get icon for policy check items
    getPolicyCheckIcon(requirement) {
      return this.policyCompliance[requirement]
        ? "check-circle"
        : "close-circle";
    },

    // Get icon type for policy check items
    getPolicyCheckType(requirement) {
      return this.policyCompliance[requirement] ? "is-success" : "is-danger";
    },

    changePassword() {
      console.log("change");

      // Validate form before submission
      if (!this.isFormValid) {
        this.$buefy.toast.open({
          message: this.$t("password_change.validation.form_incomplete"),
          type: "is-warning",
        });
        return;
      }

      // For enforced password changes, old password may not be required (temporary passwords)
      const oldPasswordRequired =
        this.askOldPassword && !this.isTemporaryPassword();

      if (oldPasswordRequired && !this.oldPassword) {
        this.$buefy.toast.open({
          message: this.$t("password_change.validation.old_password_required"),
          type: "is-danger",
        });
        return;
      }

      if (!this.newPassword || !this.confirmNewPassword) {
        this.$buefy.toast.open({
          message: this.$t("password_change.validation.passwords_required"),
          type: "is-danger",
        });
        return;
      }

      if (this.newPassword !== this.confirmNewPassword) {
        this.$buefy.toast.open({
          message: this.$t("password_change.validation.passwords_mismatch"),
          type: "is-danger",
        });
        return;
      }

      // Final policy compliance check before submission
      if (this.effectivePasswordPolicy && !this.isPasswordPolicyCompliant) {
        this.$buefy.toast.open({
          message: this.$t("password_change.validation.policy_not_met"),
          type: "is-danger",
        });
        return;
      }

      // Additional validation for enforced password changes
      if (this.isEnforced) {
        if (!this.validateEnforcedPasswordChange()) {
          return;
        }
      }

      // Emit password change event with old password (may be empty for temporary passwords)
      this.$emit("change-password", this.oldPassword, this.newPassword);
      this.resetFields();

      // Wait until the models are updated in the UI
      this.$nextTick(() => {
        this.$refs.form.reset();
      });
    },

    // Check if current password is temporary (no old password required)
    isTemporaryPassword() {
      // For enforced changes, check if old password field is not shown
      return this.isEnforced && !this.askOldPassword;
    },

    // Additional validation for enforced password changes
    validateEnforcedPasswordChange() {
      // Ensure password meets minimum security requirements
      if (!this.effectivePasswordPolicy) {
        console.warn("No password policy available for enforced change");
        return true; // Allow if no policy is set
      }

      // Check if new password is different from old password (if old password provided)
      if (this.oldPassword && this.oldPassword === this.newPassword) {
        this.$buefy.toast.open({
          message: this.$t("password_change.validation.password_must_differ"),
          type: "is-danger",
        });
        return false;
      }

      // All validations passed
      return true;
    },

    resetFields() {
      this.oldPassword = "";
      this.newPassword = "";
      this.confirmNewPassword = "";

      // Reset policy compliance
      Object.keys(this.policyCompliance).forEach((key) => {
        this.policyCompliance[key] = false;
      });
    },
  },
};
</script>

<style scoped>
.password-policy-indicators {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 0.5rem;
}

.policy-checklist {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.policy-check-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.policy-check-item:last-child {
  margin-bottom: 0;
}

.policy-check-item .icon {
  margin-right: 0.5rem;
  flex-shrink: 0;
}

/* Enhanced field styling for policy compliance */
.field.is-success .control .input {
  border-color: #48c774;
}

.field.is-success .control .input:focus {
  border-color: #48c774;
  box-shadow: 0 0 0 0.125em rgba(72, 199, 116, 0.25);
}

/* Ensure proper contrast for white text on dark backgrounds */
.has-background-eurotext-light .policy-checklist {
  color: inherit;
}

.has-background-eurotext-light .has-text-success {
  color: #48c774 !important;
}

.has-background-eurotext-light .has-text-danger {
  color: #f14668 !important;
}
</style>
